import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllResolutionType,
    getAllResolutionReasonById,
    addResolution,
    getTxnDetails
} from "../../../redux/slices/PaymentsSlice";
import { Form, Radio, Flex, Typography } from "antd";
import FormFooter from "../../../components/CustomForms/FormFooter";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import { capitalizeWords, CommentsRules } from "../../../helpers/helpers";
import { ERROR_MSGS, GLOBAL_CONSTANTS } from "../../../constants/constants";
import CustomInput from "../../../components/CustomInput/Input";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import DealDetail from "../../../components/Details/DealDetail";
import ErrorAlert from "../../../components/ErrorAlert/ErrorAlert";

const { Text } = Typography;

const ResolveForm = ({ onClose, txnData, disputeData }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const resolutionCategoryValue = Form.useWatch("resolutionCategory", form);
    const resolutionReasonValue = Form.useWatch("resolutionReason", form);
    const resolutionTypeValue = Form.useWatch("resolutionType", form);
    const {
        resolutionReason = [],
        resolutionType = [],
        resolveSubmitLoading = false,
        error,
    } = useSelector((state) => state.payments);

    useEffect(() => {
        dispatch(getAllResolutionType());
    }, [dispatch]);

    const filteredResolutionTypes = resolutionType?.filter((type) => {
        if (resolutionCategoryValue === "Resolution") {
            return (
                type.resolutionTypeId === "RTY1000002" ||
                type.resolutionTypeId === "RTY1000003"
            );
        } else if (resolutionCategoryValue === "Follow-ups") {
            return type.resolutionTypeId === "RTY1000001";
        }
        return false;
    });

    const onResCatChange = () => {
        form.resetFields([
            "resolutionType",
            "resolutionReason",
            "amount",
            "comments",
        ]);
    };

    const onResTypeChange = (value) => {
        form.setFieldsValue({ resolutionReason: undefined });
        dispatch(getAllResolutionReasonById(value));
    };

    const handleSubmit = async (values) => {
        const payload = {
            disputeId: disputeData.disputeId,
            resolutionCategory: values.resolutionCategory,
            resolutionTypeId: values.resolutionType,
            resolutionReasonId: values.resolutionReason,
            amount: values.amount && values.resolutionReason === "RRS1000003"
                ? values.amount
                : disputeData.disputeAmount,
            resolutionComments: values.comments,
        };

        const res = await dispatch(addResolution(payload));

        if (res.payload.statusCode === 201) {
            onClose();
            dispatch(getTxnDetails(txnData?.transactionId));
        }
    };

    return (
        <div className="resolve-form-container">
            <Form layout="vertical" form={form} onFinish={handleSubmit}>
                <Flex align="flex-start" justify="space-between" wrap>
                    <Text strong>Resolution Category</Text>
                    <Form.Item name="resolutionCategory" initialValue="Resolution">
                        <Radio.Group onChange={(value) => onResCatChange(value)}>
                            <Radio value="Resolution">Resolution</Radio>
                            <Radio value="Follow-ups">Follow-ups</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Flex>

                <CustomSelect
                    label="Resolution Type"
                    name="resolutionType"
                    isRequired={true}
                    onChange={(value) => onResTypeChange(value)}
                    placeholder="Select Resolution Type"
                    options={filteredResolutionTypes?.map((type) => ({
                        value: type.resolutionTypeId,
                        label: capitalizeWords(type.resolutionTypeDesc),
                    }))}
                    rules={[{ required: true, message: ERROR_MSGS.SEL_RES_TYPE_ERR_MSG }]}
                />

                <CustomSelect
                    label="Resolution Reason"
                    name="resolutionReason"
                    isRequired={true}
                    placeholder="Select Resolution Reason"
                    options={resolutionReason?.map((type) => ({
                        value: type.resolutionReasonId,
                        label: capitalizeWords(type.resolutionReasonDesc),
                    }))}
                    rules={[
                        { required: true, message: ERROR_MSGS.SEL_RES_REASON_ERR_MSG },
                    ]}
                />

                {resolutionReasonValue === "RRS1000003" && (
                    <CustomInputNumber
                        label="Amount to be Credited"
                        name="amount"
                        isRequired={true}
                        controls={false}
                        placeholder="Enter Amount"
                        rules={[
                            { required: true, message: ERROR_MSGS.AMT_ERR_MSG },
                            {
                                validator(_, value) {
                                    if (String(value).length > 15) {
                                        return Promise.reject(
                                            new Error(`Amount cannot exceed 15 digits.`)
                                        );
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    />
                )}

                <CustomInput
                    label="Resolution Comments"
                    name="comments"
                    rows={3}
                    isRequired={true}
                    isTextArea={true}
                    type="text"
                    placeholder="Enter your Comments"
                    rules={CommentsRules}
                />

                {resolutionCategoryValue === "Resolution" &&
                    resolutionTypeValue === "RTY1000002" && (
                        <div className="resolve-details-container">
                            <Flex gap={15} vertical>
                                {resolutionReasonValue !== "RRS1000003" && (
                                    <DealDetail
                                        label="Refund Amount"
                                        value={disputeData.disputeAmount || GLOBAL_CONSTANTS.NA}
                                    />
                                )}
                                <DealDetail
                                    label="Credit Account Name"
                                    value={disputeData?.accHolderName || GLOBAL_CONSTANTS.NA}
                                />
                                <DealDetail
                                    label="Credit Account Number"
                                    value={disputeData?.accNum || GLOBAL_CONSTANTS.NA}
                                />
                            </Flex>
                        </div>
                    )}

                <div style={{marginTop: 20}}>
                    {error && <ErrorAlert error={error} />}
                </div>

                <FormFooter
                    onCancelClick={() => {
                        onClose();
                        form.resetFields();
                    }}
                    submitLoading={resolveSubmitLoading}
                    btnName={
                        resolutionCategoryValue === "Follow-ups"
                            ? "Update dispute comment"
                            : resolutionTypeValue === "RTY1000003"
                                ? "Confirm Reject"
                                : "Confirm Refund"
                    }
                    btnType={resolutionTypeValue === "RTY1000003" && "danger"}
                />
            </Form>
        </div>
    );
};

export default ResolveForm;
